import {
    type AccountSummary,
    type HolisticSummaryCategory,
    Provider,
} from '@fruitful/types';
import { AnimatedScrollView } from '@kanelloc/react-native-animated-header-scroll-view';
import { useFeatureGate } from '@statsig/expo-bindings';
import { Redirect, router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { capitalize } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodySmallEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    HolisticAccountLogo,
    Icon,
    Input,
    isNonMobileWeb,
    PageBelowHeader,
    PenLineIcon,
    RotateCcwIcon,
    Spinner,
    UnlinkIcon,
    useToastController,
    View,
    X,
    YStack,
} from 'smoothie';
import { useDebouncedCallback } from 'use-debounce';

import { findAccountByGroupedAccountId } from '../../../common/utils/holisticUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { captureException } from '../../../logger/Logger';
import useConnectQuilttInstitution from '../../hooks/useConnectQuilttInstitution';
import useGetHolisticSummary from '../../hooks/useGetHolisticSummary';
import { useGetHolisticTransactions } from '../../hooks/useGetHolisticTransactions';
import useRecategorizeAccount from '../../hooks/useRecategorizeAccount';
import useReconnectQuilttInstitution from '../../hooks/useReconnectQuilttInstitution';
import useRenameAccount from '../../hooks/useRenameAccount';
import { useSearchTransactions } from '../../hooks/useSearchTransactions';
import useToggleAccountVisibility from '../../hooks/useToggleAccountVisibility';
import {
    holisticSummaryAtom,
    selectedHolisticAccountAtom,
} from '../../state/HolisticAtoms';
import type {
    SpendAccountCategory,
    Transaction,
} from '../../types/HolisticSummary';
import EditAccountSheet, { EditSheetPage } from '../EditAccountSheet';
import { HolisticAccountHeader } from '../headers/HolisticAccountHeader';
import { HolisticNavHeader } from '../headers/HolisticNavHeader';
import TransactionsList from './TransactionsList';

const Transactions = ({
    category,
    id,
    connectionId,
}: {
    category: HolisticSummaryCategory;
    id: string;
    connectionId: string;
}) => {
    const [transactions, setTransactions] = useState<Transaction[] | undefined>(
        undefined,
    );
    const [searchedTransactions, setSearchedTransactions] = useState<
        Transaction[] | undefined
    >(undefined);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [account, setAccount] = useState<AccountSummary | undefined>(
        undefined,
    );
    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
    const [editSheetPage, setEditSheetPage] = useState(EditSheetPage.EDIT);
    const setSelectedHolisticAccount = useSetAtom(selectedHolisticAccountAtom);

    const { connectInstitution } = useConnectQuilttInstitution();
    const { reconnectInstitution } =
        useReconnectQuilttInstitution(connectionId);

    const { value: enableTransactionSearch } = useFeatureGate(
        FeatureFlags.ENABLE_TRANSACTION_SEARCH,
    );

    const updateDebouncedSearchTerm = useDebouncedCallback((term: string) => {
        setDebouncedSearchTerm(term);
    }, 250);

    useEffect(() => {
        if (searchTerm === '') {
            updateDebouncedSearchTerm.cancel();
            setDebouncedSearchTerm('');
        } else {
            updateDebouncedSearchTerm(searchTerm);
        }
    }, [searchTerm, updateDebouncedSearchTerm]);

    const { data: searchResponse, isLoading: isSearching } =
        useSearchTransactions(
            {
                searchTerm: debouncedSearchTerm,
                limit: 50,
                accountId: account ? account.id : undefined,
            },
            {
                enabled: debouncedSearchTerm !== '',
            },
        );

    useEffect(() => {
        if (searchResponse?.transactions) {
            setSearchedTransactions(searchResponse.transactions);
        }
    }, [searchResponse]);

    useEffect(() => {
        if (searchTerm === '') {
            setSearchedTransactions(undefined);
        }
    }, [searchTerm]);

    const getRelinkingFlow = (id: string) => {
        const accounts = holisticSummary?.[category]?.accounts;
        const fetchedAccount = findAccountByGroupedAccountId(
            category,
            accounts ?? [],
            id,
        );

        if (!fetchedAccount) {
            toast.show('Error finding account', {
                duration: 2000,
            });
            return () => {};
        }

        const { provider } = fetchedAccount;

        if (provider === Provider.QUILTT) {
            return reconnectInstitution;
        }
        return connectInstitution;
    };

    const holisticSummary = useAtomValue(holisticSummaryAtom);
    const {
        refetch: refetchHolisticSummary,
        isLoading: isLoadingHolisticSummary,
        isError: holisticSummaryHasError,
    } = useGetHolisticSummary(!holisticSummary?.[category]);

    useEffect(() => {
        const accounts = holisticSummary?.[category]?.accounts;
        const account = findAccountByGroupedAccountId(
            category,
            accounts ?? [],
            id,
        );

        if (!isLoadingHolisticSummary && accounts) {
            if (account) {
                setAccount(account);
            } else {
                router.back();
            }
        }
    }, [category, holisticSummary?.[category]?.accounts, id]);

    const toast = useToastController();

    const { mutateAsync: renameAccount, isPending: isLoadingRename } =
        useRenameAccount();
    const {
        mutateAsync: recategorizeAccount,
        isPending: isLoadingRecategorize,
    } = useRecategorizeAccount();
    const { mutateAsync: toggleAccountVisibility } =
        useToggleAccountVisibility();

    const onSubmitRename = async (
        accountId: string,
        provider: Provider,
        rename: string,
    ) => {
        try {
            await renameAccount({
                body: {
                    accountId,
                    provider,
                    name: rename,
                },
            });
            void refetchHolisticSummary();
            toast.show('Account renamed', {
                duration: 2000,
                icon: (
                    <View
                        backgroundColor="$gray200"
                        padding={8}
                        borderRadius={4}
                    >
                        <Icon icon={PenLineIcon} size={20} color="$gray1100" />
                    </View>
                ),
            });
        } catch (e) {
            captureException(e);
            toast.show('Error renaming account', {
                duration: 2000,
            });
        }
    };

    const onSubmitRecategorizeAccount = async (
        account: AccountSummary,
        holisticCategory: HolisticSummaryCategory,
        spendAccountCategory?: SpendAccountCategory,
    ) => {
        try {
            const {
                id: accountId,
                name,
                institutionName,
                logo,
                provider,
            } = account;
            await recategorizeAccount({
                body: {
                    accountId,
                    provider,
                    holisticCategory,
                    spendAccountCategory,
                },
            });
            void refetchHolisticSummary();
            toast.show(
                `${name ?? 'Account'} moved to ${capitalize(holisticCategory)}`,
                {
                    duration: 2000,
                    icon: (
                        <HolisticAccountLogo
                            institutionName={institutionName}
                            logo={logo}
                            provider={provider}
                        />
                    ),
                },
            );
        } catch (e) {
            captureException(e);
            toast.show('Error recategorizing account', {
                duration: 2000,
            });
        }
    };

    const onSubmitHideAccount = async (
        accountId: string,
        provider: Provider,
        isLastAccountOnLink: boolean,
    ) => {
        try {
            await toggleAccountVisibility({
                body: {
                    accountId,
                    provider,
                    isHidden: true,
                },
            });

            void refetchHolisticSummary();

            toast.show('Account has been hidden', {
                duration: 4000,
                icon: (
                    <View
                        backgroundColor="$gray100"
                        padding={8}
                        borderRadius={4}
                    >
                        <Icon icon={UnlinkIcon} size={20} color="$gray1100" />
                    </View>
                ),
                ...(isLastAccountOnLink
                    ? {}
                    : {
                          button: (
                              <Button
                                  variant={ButtonVariant.PRIMARY}
                                  size={ButtonSize.SMALL}
                                  text="Undo"
                                  icon={RotateCcwIcon}
                                  onPress={() => {
                                      void handleUndoHide(accountId, provider);
                                  }}
                                  width="auto"
                              />
                          ),
                      }),
            });

            // check what category account was in. if category is now empty, go home. otherwise, go back.
            // we need to filter out the account that was just hidden, because it's very likely the atom hasn't yet been updated after the holistic summary fetch
            const wasLastAccountInCategory =
                (holisticSummary?.[category]?.accounts ?? []).filter(
                    (account) => account.id !== accountId,
                ).length < 1;

            if (wasLastAccountInCategory) {
                router.navigate('/home');
                return;
            }
            router.back();
        } catch (e) {
            // Still refresh in case this call partially worked
            void refetchHolisticSummary();

            captureException(e);
            toast.show('Error unlinking account', {
                duration: 2000,
            });
        }
    };

    const handleUndoHide = async (accountId: string, provider: Provider) => {
        try {
            toast.hide();
            await toggleAccountVisibility({
                body: {
                    accountId,
                    provider,
                    isHidden: false,
                },
            });
            void refetchHolisticSummary();
        } catch (e) {
            captureException(e);
            toast.show('Error undoing unlink account', {
                duration: 2000,
            });
        }
    };

    const {
        data: fetchedTransactions,
        isLoading: isLoadingTransactions,
        isError,
        error,
    } = useGetHolisticTransactions(account);

    useEffect(() => {
        setTransactions(fetchedTransactions ?? []);
    }, [fetchedTransactions?.length ?? 0]);

    const insets = useSafeAreaInsets();

    const displayErrorMessageForNoTransactions =
        error?.message === 'Member does not own account'
            ? 'This account was linked by your partner. You can only view transactions for accounts you have linked.'
            : 'You do not have permission to view transactions for this account';

    const resultsHeader = useMemo(() => {
        if (!account || isLoadingTransactions) return '';
        if (searchTerm !== '') return `Search results for "${searchTerm}":`;

        return (transactions ?? []).length > 0
            ? 'Recent posted transactions:'
            : isError
              ? displayErrorMessageForNoTransactions
              : !isLoadingTransactions &&
                  (account.provider === Provider.SYNCTERA ||
                      account.provider === Provider.QUILTT)
                ? 'No recent transactions'
                : 'Transactions are not yet available for this account';
    }, [searchTerm, transactions, isLoadingTransactions, isError]);

    if (holisticSummaryHasError) {
        return <Redirect href="/" />;
    }

    if (
        isLoadingHolisticSummary ||
        isLoadingRename ||
        isLoadingRecategorize ||
        !account
    ) {
        return <FullScreenSpinner />;
    }

    const topBarHeight = (insets.top || 16) + 48;

    return (
        <YStack backgroundColor="$gray100" flex={1}>
            <AnimatedScrollView
                style={{ flex: 1 }}
                isHeaderComponentFixed={true}
                isFullScreen={true}
                contentContainerStyle={{ flexGrow: 1 }}
                showsVerticalScrollIndicator={isNonMobileWeb}
                alwaysBounceVertical={false}
                topBarHeight={topBarHeight}
                headerMaxHeight={436}
                HeaderComponent={
                    <HolisticAccountHeader
                        account={account}
                        onEditPressed={() => {
                            if (
                                account.provider === Provider.QUILTT ||
                                account.provider === Provider.SPINWHEEL
                            ) {
                                setSelectedHolisticAccount(account);
                                router.navigate(
                                    '/holistic/edit-institution-account',
                                );
                            } else {
                                setIsEditSheetOpen(true);
                            }
                        }}
                        onReconnectPressed={() => {
                            getRelinkingFlow(id)();
                        }}
                    />
                }
                HeaderNavbarComponent={
                    <HolisticNavHeader
                        isScrolled={false}
                        height={topBarHeight}
                    />
                }
                TopNavBarComponent={
                    <HolisticNavHeader
                        title={account.name}
                        isScrolled={true}
                        height={topBarHeight}
                    />
                }
            >
                {/* Hide transactions for Spinwheel accounts */}
                {account.provider !== Provider.SPINWHEEL && (
                    <PageBelowHeader
                        flex={1}
                        width="100%"
                        maxWidth={700}
                        $sm={{
                            height: 'auto',
                            paddingTop: 40,
                            paddingHorizontal: 32,
                            borderRadius: 32,
                            marginBottom: 80,
                        }}
                    >
                        {enableTransactionSearch && (
                            <Input
                                value={searchTerm}
                                onChangeText={(v) => {
                                    setSearchTerm(v);
                                }}
                                placeholder="Search"
                                rightIcon={X}
                                onPressIcon={() => {
                                    setSearchTerm('');
                                    setDebouncedSearchTerm('');
                                }}
                            />
                        )}
                        <BodySmallEmphasized
                            marginTop={4}
                            marginBottom={12}
                            textAlign="center"
                            flex={1}
                        >
                            {resultsHeader}
                        </BodySmallEmphasized>
                        {isLoadingTransactions ||
                        isSearching ||
                        transactions === undefined ? (
                            <YStack flex={1} padding={50}>
                                <Spinner />
                            </YStack>
                        ) : (
                            <Animated.View entering={FadeIn.duration(500)}>
                                <TransactionsList
                                    transactions={
                                        searchedTransactions ?? transactions
                                    }
                                />
                            </Animated.View>
                        )}
                    </PageBelowHeader>
                )}
            </AnimatedScrollView>
            {isEditSheetOpen && (
                <EditAccountSheet
                    account={account}
                    page={editSheetPage}
                    setPage={setEditSheetPage}
                    onClose={() => {
                        setIsEditSheetOpen(false);
                        setEditSheetPage(EditSheetPage.EDIT);
                    }}
                    onSubmitRename={onSubmitRename}
                    onSubmitRecategorizeAccount={onSubmitRecategorizeAccount}
                    onSubmitHideAccount={onSubmitHideAccount}
                    onPressRelink={() => {
                        setIsEditSheetOpen(false);
                        getRelinkingFlow(id)();
                    }}
                />
            )}
        </YStack>
    );
};

export default Transactions;
